import { Helmet } from 'react-helmet';
import { useActiveDeals } from '@/hooks/useDeals';
import { DealCard } from '@/components/deals/DealCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Tag } from 'lucide-react';

export default function DealsPage() {
  const { data: deals, isLoading } = useActiveDeals();

  const categories = [...new Set(deals?.map(d => d.partner?.category).filter(Boolean))];

  return (
    <>
      <Helmet>
        <title>Travel Deals & Discounts | Trotting the World</title>
        <meta name="description" content="Exclusive travel deals and discounts on flights, hotels, tours, and more. Save money on your next adventure with our curated partner offers." />
      </Helmet>

      <div className="container px-4 sm:px-6 mx-auto py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Exclusive Offers</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">Travel Deals & Discounts</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Save money on your next adventure with exclusive discounts from our trusted travel partners.
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
              <Tag className="w-3 h-3 mr-1" />
              All Deals
            </Badge>
            {categories.map((category) => (
              <Badge key={category} variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
                {category}
              </Badge>
            ))}
          </div>
        )}

        {/* Deals Grid */}
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-64 w-full rounded-lg" />
            ))}
          </div>
        ) : deals && deals.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Active Deals</h2>
            <p className="text-muted-foreground">
              Check back soon for new exclusive travel deals and discounts!
            </p>
          </div>
        )}

        {/* Disclosure */}
        <div className="mt-12 p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <p>
            <strong>Disclosure:</strong> Some links on this page are affiliate links. 
            We may earn a commission if you make a purchase through these links, at no extra cost to you.
          </p>
        </div>
      </div>
    </>
  );
}
